import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { promptInstall, useInstallMode } from "@/offline/install";
import { goOnline, listOutbox, syncOutbox } from "@/offline/outbox";
import CycleBadge from "@/pages/dashboard/users/components/CycleBadge";
import SchoolYearSelect from "./SchoolYearSelect";
import HeaderNotifications from "./HeaderNotifications";
import HeaderSearch from "./HeaderSearch";
import OfflineIndicator, { GoOfflineDialog } from "./OfflineIndicator";
import { useOfflineStatus } from "@/offline/status";
import { initials, plural } from "@/utils/format";

interface HeaderProps {
  onMenu: () => void;
}

export default function Header({ onMenu }: HeaderProps) {
  const navigate = useNavigate();
  const { user, role, activeSchool, isPlatformAdmin, logout } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [schoolOpen, setSchoolOpen] = useState(false);
  /** Actions pas encore envoyées au moment de se déconnecter (la déconnexion les effacerait). */
  const [unsent, setUnsent] = useState(0);
  const installMode = useInstallMode();
  const [iosHelp, setIosHelp] = useState(false);
  const { forced } = useOfflineStatus();
  const [goOffline, setGoOffline] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Les actions gardées sur l'appareil partent d'abord ; s'il en reste (hors ligne), on demande confirmation.
  const requestSignOut = async () => {
    setUserOpen(false);
    await syncOutbox();
    const left = (await listOutbox()).length;
    if (left > 0) setUnsent(left);
    else await signOut();
  };

  // Menu du compte : un clic (ou un toucher) à côté le ferme, Échap aussi.
  useEffect(() => {
    if (!userOpen) return;
    const onDown = (e: PointerEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setUserOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [userOpen]);

  const displayName = user ? `${user.firstName} ${user.lastName}` : "Non connecté";
  const userInitials = user ? initials(user.lastName, user.firstName) : "—";

  const closeAll = () => {
    setNotifOpen(false);
    setUserOpen(false);
    setSchoolOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-background-200 bg-background-50/85 pt-[env(safe-area-inset-top,0px)] backdrop-blur-md supports-[backdrop-filter]:bg-background-50/75">
      <div className="flex h-16 items-center justify-between gap-2 px-3 sm:gap-4 sm:px-4 md:px-6 lg:px-8">
      <div className="flex min-w-0 items-center gap-1 sm:gap-3">
        <button
          type="button"
          onClick={onMenu}
          className="press w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 lg:hidden cursor-pointer"
          aria-label="Ouvrir le menu"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
        <HeaderSearch />
      </div>

      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 md:gap-3">
        {isPlatformAdmin ? (
          <Badge tone="accent" icon="ri-shield-star-line">
            Console plateforme
          </Badge>
        ) : (
          <>
            <OfflineIndicator />
            <SchoolYearSelect />
          </>
        )}

        <HeaderNotifications
          open={notifOpen}
          onToggle={() => {
            const next = !notifOpen;
            closeAll();
            setNotifOpen(next);
          }}
          onClose={() => setNotifOpen(false)}
        />

        <div className="relative" ref={userRef}>
          <button
            onClick={() => {
              const next = !userOpen;
              closeAll();
              setUserOpen(next);
            }}
            type="button"
            aria-haspopup="menu"
            aria-expanded={userOpen}
            className="press flex items-center gap-2 rounded-md px-1 py-1 hover:bg-background-100 cursor-pointer sm:px-1.5"
          >
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={displayName}
                title={displayName}
                className="w-9 h-9 rounded-full object-top object-cover border border-background-200 shrink-0"
              />
            ) : (
              <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-800 flex items-center justify-center text-xs font-semibold">
                {userInitials}
              </span>
            )}
            <span className="hidden md:block text-left leading-tight">
              <span className="block text-xs font-medium text-foreground-900">{displayName}</span>
              <span className="block text-xs text-foreground-500">{role?.name ?? "Aucun rôle"}</span>
            </span>
            <i className="ri-arrow-down-s-line text-foreground-500 hidden md:block"></i>
          </button>
          {userOpen && (
            <div role="menu" className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-1.5rem)] origin-top-right animate-scale-in rounded-lg border border-background-200 bg-background-50 p-1.5 shadow-pop">
              <div className="rounded-md bg-background-100/70 px-3 py-2.5">
                <p className="text-sm font-medium text-foreground-900 break-words">{displayName}</p>
                <p className="text-xs text-foreground-500 break-all">{user?.email ?? "—"}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <CycleBadge scope={role?.cycleScope ?? "GLOBAL"} />
                  {isPlatformAdmin ? (
                    <Badge tone="accent">Super-admin plateforme</Badge>
                  ) : (
                    <Badge tone="neutral" icon="ri-building-2-line">
                      {activeSchool?.shortName ?? "Aucune école"}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mt-1.5 border-t border-background-200 pt-1">
                {!isPlatformAdmin && (
                  <button
                    onClick={() => {
                      setUserOpen(false);
                      if (forced) void goOnline();
                      else setGoOffline(true);
                    }}
                    role="menuitem"
                    className="flex w-full min-h-[2.75rem] items-center gap-2.5 rounded-md px-3 text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 cursor-pointer sm:min-h-[2.25rem]"
                  >
                    <i className={forced ? "ri-wifi-line" : "ri-wifi-off-line"}></i>
                    {forced ? "Repasser en ligne" : "Passer en mode hors ligne"}
                  </button>
                )}
                {installMode && (
                  <button
                    onClick={() => {
                      setUserOpen(false);
                      if (installMode === "ios") setIosHelp(true);
                      else void promptInstall();
                    }}
                    role="menuitem"
                    className="flex w-full min-h-[2.75rem] items-center gap-2.5 rounded-md px-3 text-sm text-primary-700 hover:bg-primary-50 active:bg-primary-100 cursor-pointer sm:min-h-[2.25rem]"
                  >
                    <i className="ri-download-2-line"></i> Installer l'application
                  </button>
                )}
                <button
                  onClick={() => {
                    setUserOpen(false);
                    navigate("/dashboard/account");
                  }}
                  role="menuitem"
                  className="flex w-full min-h-[2.75rem] items-center gap-2.5 rounded-md px-3 text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 cursor-pointer sm:min-h-[2.25rem]"
                >
                  <i className="ri-user-settings-line"></i> Mon compte
                </button>
                <button
                  onClick={() => void requestSignOut()}
                  role="menuitem"
                  className="flex w-full min-h-[2.75rem] items-center gap-2.5 rounded-md px-3 text-sm text-accent-700 hover:bg-accent-50 active:bg-accent-100 cursor-pointer sm:min-h-[2.25rem]"
                >
                  <i className="ri-logout-box-r-line"></i> Se déconnecter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      <ConfirmDialog
        open={unsent > 0}
        title="Actions pas encore envoyées"
        message={`${plural(unsent, "action")} (notes, paiements ou inscriptions) ${
          unsent > 1 ? "n'ont pas encore été envoyées" : "n'a pas encore été envoyée"
        } au serveur. Se déconnecter maintenant ${unsent > 1 ? "les effacerait" : "l'effacerait"} de cet appareil : retrouvez d'abord la connexion.`}
        confirmLabel="Se déconnecter et les effacer"
        onConfirm={() => void signOut()}
        onClose={() => setUnsent(0)}
      />
      <GoOfflineDialog open={goOffline} onClose={() => setGoOffline(false)} />
      <Modal
        open={iosHelp}
        onClose={() => setIosHelp(false)}
        title="Installer WaitechEdu sur l'iPhone"
        icon="ri-download-2-line"
        size="sm"
        footer={<Button onClick={() => setIosHelp(false)}>J'ai compris</Button>}
      >
        <ol className="space-y-3 text-sm text-foreground-700">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">1</span>
            <span>
              Ouvrez WaitechEdu dans <strong>Safari</strong>, puis touchez le bouton <strong>Partager</strong>{" "}
              <i className="ri-share-box-line" aria-hidden="true"></i> en bas de l'écran.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">2</span>
            <span>
              Choisissez <strong>« Sur l'écran d'accueil »</strong>, puis <strong>Ajouter</strong>.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">3</span>
            <span>WaitechEdu s'ouvre ensuite depuis son icône, en plein écran, même sans connexion.</span>
          </li>
        </ol>
      </Modal>
    </header>
  );
}